/**
 * インポートしたスケッチ（TV/Taskviewが配るXDD等のタスク図形、
 * OziExplorerの`.plt`形式）。
 *
 * 会場のPZ/SAのような「あらかじめ用意されたもの」ではなく、当日
 * 利用者がファイルで受け取って読み込む一時的な地図オーバーレイ。
 * IGCの読み込み（`loadFlightFromFile`）と同じく、LINE等でzipに
 * 包まれて届いても読めるようにする。
 *
 * ★ 大会中は日ごと・タスクごとに複数回インポートする（利用者からの
 *   指摘、2026-09-13: 「後で一括で消せるようにして」）ので、1件を
 *   置き換えるのではなく一覧として溜め、まとめて消せるようにしてある。
 */

import { extractOziPltTextFromZip, looksLikeZip, parseOziPlt, type OziPlt } from '@gv4d/core';

export type { OziPlt } from '@gv4d/core';

/** 一覧に溜める1件ぶん。`id`は読み込み順の管理・一覧のkeyに使う */
export interface SketchEntry extends OziPlt {
  id: string;
}

/** スケッチファイルを利用者が直接読み込む経路（zipに包まれていてもそのまま読める） */
export async function loadSketchFromFile(file: File): Promise<OziPlt> {
  const head = new Uint8Array(await file.slice(0, 4).arrayBuffer());
  const text = looksLikeZip(head)
    ? await extractOziPltTextFromZip(new Uint8Array(await file.arrayBuffer()))
    : await file.text();
  const sketch = parseOziPlt(text);
  if (!sketch) throw new Error('OziExplorerの.plt形式として読めませんでした');
  if (sketch.polygons.length === 0) throw new Error('図形が含まれていません');
  return sketch;
}

/**
 * 表示用GeoJSONに変換する。溜まっている全件を1つのFeatureCollectionに
 * まとめる（1件＝1フィーチャー。複数の閉多角形は、文字を象る図形など、
 * 穴あきになりうるため1つのMultiPolygonにまとめてある）。色はファイル
 * 自身が持つ色をそのままプロパティに乗せ、layers.ts側で
 * `['get', 'color']`として使う。
 */
export function sketchesToGeoJson(entries: readonly SketchEntry[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: entries.map((sketch) => ({
      type: 'Feature',
      properties: { id: sketch.id, name: sketch.name, color: sketch.color },
      geometry: {
        type: 'MultiPolygon',
        coordinates: sketch.polygons.map((p) => [p.ring]),
      },
    })),
  };
}

// --- 保存 -------------------------------------------------------------------

const KEY_PREFIX = 'gv4d-sketch:';

function isValidSketchEntry(v: unknown): v is SketchEntry {
  const p = v as Partial<SketchEntry> | null;
  return (
    !!p &&
    typeof p.id === 'string' &&
    typeof p.name === 'string' &&
    typeof p.color === 'string' &&
    Array.isArray(p.polygons) &&
    p.polygons.every((poly) => Array.isArray(poly?.ring))
  );
}

/**
 * インポートしたスケッチは会場ごとに保存する。会場を切り替えたときに
 * 前の会場のスケッチが地図の外に残らないようにする（goals.tsと同じ理由）。
 */
export function loadSavedSketches(venueId: string): SketchEntry[] {
  try {
    const raw = localStorage.getItem(KEY_PREFIX + venueId);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isValidSketchEntry) : [];
  } catch {
    return [];
  }
}

export function saveSketches(venueId: string, entries: readonly SketchEntry[]): void {
  try {
    localStorage.setItem(KEY_PREFIX + venueId, JSON.stringify(entries));
  } catch {
    // 保存できなくても、その場の表示は続けられる
  }
}

export function clearSavedSketches(venueId: string): void {
  try {
    localStorage.removeItem(KEY_PREFIX + venueId);
  } catch {
    // ここが失敗しても致命的ではない
  }
}
